import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface TimezoneDetails {
  label: string;
  abbreviation: string;
  value: string;
}

export const getDefaultTimezone = (): TimezoneDetails => {
  try {
    // Get user's timezone from browser
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Calculate offset
    const now = new Date();
    const offset = -now.getTimezoneOffset(); // in minutes
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? '+' : '-';
    const offsetString = `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    // Get timezone abbreviation (like EST, PST, etc.)
    const abbreviation =
      new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        timeZoneName: 'short',
      })
        .formatToParts(now)
        .find((part) => part.type === 'timeZoneName')?.value || userTimezone;

    return {
      label: `${userTimezone} (${offsetString})`,
      abbreviation: abbreviation,
      value: userTimezone,
    };
  } catch (error) {
    // Fallback to UTC if timezone detection fails
    console.warn('Failed to detect user timezone, falling back to UTC', error);
    return {
      label: 'UTC (+00:00)',
      abbreviation: 'UTC',
      value: 'UTC',
    };
  }
};

const getSelectedTimeZone = (): TimezoneDetails | null => {
  const raw = localStorage.getItem('selectedTimeZone');
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const initialState = getSelectedTimeZone() ?? getDefaultTimezone();

const timeZoneSlice = createSlice({
  name: 'timeZone',
  initialState,
  reducers: {
    setTimeZone: (_state, action: PayloadAction<TimezoneDetails>) => {
      localStorage.setItem('selectedTimeZone', JSON.stringify(action.payload));
      return action.payload;
    },
  },
});

export const { setTimeZone } = timeZoneSlice.actions;
export default timeZoneSlice.reducer;
